import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AiApiKeyIn,
  AiAssistant,
  AiAssistantCreate,
  AiAssistantListResponse,
  AiAssistantUpdate,
  AiAssignmentIn,
  AiAssignmentListResponse,
  AiCostSummaryList,
  AiMessageListResponse,
  MyAssistantResponse,
} from "@/types/admin/ai";
import { aiAssistantKeys } from "./keys";

export function useMyAssistant() {
  return useQuery({
    queryKey: aiAssistantKeys.me(),
    queryFn: () => api.get<MyAssistantResponse>("/ai-assistants/me"),
    staleTime: 60_000, // 1 min — altera raramente
  });
}

export function useAiAssistants() {
  return useQuery({
    queryKey: aiAssistantKeys.all,
    queryFn: () => api.get<AiAssistantListResponse>("/ai-assistants"),
  });
}

export function useAiAssistant(id: number | undefined) {
  return useQuery({
    queryKey: aiAssistantKeys.detail(id),
    queryFn: () => api.get<AiAssistant>(`/ai-assistants/${id}`),
    enabled: id !== undefined,
  });
}

export function useUpdateAiAssistant(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AiAssistantUpdate) =>
      api.patch<AiAssistant>(`/ai-assistants/${id}`, data),
    onSuccess: (updated) => {
      qc.setQueryData(aiAssistantKeys.detail(id), updated);
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}

export function useAiAssistantAssignments(assistantId: number | undefined) {
  return useQuery({
    queryKey: aiAssistantKeys.assignments(assistantId),
    enabled: assistantId !== undefined,
    queryFn: () =>
      api.get<AiAssignmentListResponse>(
        `/ai-assistants/${assistantId}/assignments`
      ),
  });
}

export function useAssignAiAssistantUser(assistantId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AiAssignmentIn) =>
      api.post(`/ai-assistants/${assistantId}/assign`, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: aiAssistantKeys.assignments(assistantId),
      });
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}

export function useRemoveAiAssistantAssignment(assistantId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/ai-assistants/${assistantId}/assign/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: aiAssistantKeys.assignments(assistantId),
      });
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}

export function useAiAssistantMessages(
  assistantId: number | undefined,
  params?: { user_id?: string; skip?: number; limit?: number }
) {
  return useQuery({
    queryKey: aiAssistantKeys.messages(assistantId, params),
    queryFn: () =>
      api.get<AiMessageListResponse>(
        `/ai-assistants/${assistantId}/messages`,
        params as Record<string, unknown>
      ),
    enabled: assistantId !== undefined,
  });
}

export function useAiCostSummary(assistantId: number | undefined) {
  return useQuery({
    queryKey: aiAssistantKeys.costs(assistantId),
    queryFn: () =>
      api.get<AiCostSummaryList>(`/ai-assistants/${assistantId}/costs`),
    enabled: assistantId !== undefined,
  });
}

export function useCreateAiAssistant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AiAssistantCreate) =>
      api.post<AiAssistant>("/ai-assistants", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}

export function useDeleteAiAssistant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/ai-assistants/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}

export function useSetAiAssistantApiKey(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AiApiKeyIn) =>
      api.put<AiAssistant>(`/ai-assistants/${id}/api-key`, data),
    onSuccess: (updated) => {
      qc.setQueryData(aiAssistantKeys.detail(id), updated);
      qc.invalidateQueries({ queryKey: aiAssistantKeys.all });
    },
  });
}
